<!-- src/shared/components/forms/FeedbackModalForm.vue -->

<script setup lang="ts">
  import { useI18n } from 'vue-i18n';
import { useFormSubmission } from '@/shared/composables/useFormSubmission';
import { useBootstrapStore } from '@/shared/stores/bootstrapStore';
import { useCsrfStore } from '@/shared/stores/csrfStore';
import { useMediaQuery } from '@vueuse/core';
import { computed, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';

  const { t } = useI18n();
  const csrfStore = useCsrfStore();
  const bootstrapStore = useBootstrapStore();
  const { cust, ot_version_long } = storeToRefs(bootstrapStore);

  export interface Props {
    enabled?: boolean;
    showRedButton: boolean | null;
  }

  withDefaults(defineProps<Props>(), {
    enabled: true,
    showRedButton: false,
  });

  const userTimezone = ref('');
  const feedbackMessage = ref('');

  // Mirror of V3::Logic::ReceiveFeedback::MAX_MSG_LENGTH in
  // apps/api/v3/logic/feedback.rb. Keep these in sync — the server silently
  // truncates anything beyond this length.
  const MAX_MSG_LENGTH = 200_000;
  const CHAR_WARNING_THRESHOLD = Math.floor(MAX_MSG_LENGTH * 0.9);

  const charactersRemaining = computed(() => MAX_MSG_LENGTH - feedbackMessage.value.length);
  const showCharacterCounter = computed(() => feedbackMessage.value.length >= CHAR_WARNING_THRESHOLD);
  const atCharacterLimit = computed(() => feedbackMessage.value.length >= MAX_MSG_LENGTH);

  const resetForm = () => {
    feedbackMessage.value = '';
    // Reset other non-hidden form fields here if you have any
  };

  onMounted(() => {
    userTimezone.value = Intl.DateTimeFormat().resolvedOptions().timeZone;
  });

  const emit = defineEmits(['feedback-sent']);

  const { isSubmitting, error, success, submitForm } = useFormSubmission({
    url: '/api/v3/feedback',
    successMessage: t('web.LABELS.feedback_received'),
    onSuccess: (data: unknown) => {
      console.debug('Feedback sent:', data);
      emit('feedback-sent');
      resetForm();
    },
    onError: (data: unknown) => {
      console.error('Error sending feedback:', data);
    },
  });

  const form = ref<HTMLFormElement | null>(null);

  const handleKeydown = (event: KeyboardEvent) => {
    if (event.key === t('web.COMMON.enter') && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      form.value?.requestSubmit(); // This triggers the form submission event
    }
  };

  // Submit form UI

  /**
   * Computed property to determine the submit key combination text based on the platform
   */
  const submitWithText = computed(() => navigator.platform.includes(t('web.COMMON.mac')) ? t('web.COMMON.enter_0') : t('web.COMMON.ctrl_enter'));

  /**
   * State to track if the device is a desktop using useMediaQuery
   */
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  const submitWithCheck = async (event?: Event) => {
    console.debug('Submitting feedback form');

    await submitForm(event);
  };

  const buttonText = computed(() =>
    isSubmitting.value ? t('web.LABELS.sending_ellipses') : t('web.COMMON.button_send_feedback')
  );
</script>

<template>
  <div class="space-y-8">
    <!-- Feedback Form -->
    <form
      ref="form"
      @submit.prevent="submitWithCheck"
      class="space-y-6">
      <input
        type="hidden"
        name="utf8"
        value="✓" />
      <input
        type="hidden"
        name="shrimp"
        :value="csrfStore.shrimp" />
      <div>
        <label
          for="feedback-message"
          class="sr-only">
          {{ t('web.feedback.your_feedback') }}
        </label>
        <!-- prettier-ignore-attribute class -->
        <textarea
          id="feedback-message"
          v-model="feedbackMessage"
          class="w-full rounded-md border border-gray-300
            bg-gray-50 px-3 py-2 text-gray-900 transition-colors placeholder:text-gray-400
            focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500
            dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          name="msg"
          rows="4"
          required
          :maxlength="MAX_MSG_LENGTH"
          @keydown="handleKeydown"
          :placeholder="t('web.COMMON.feedback_text')"
          :aria-label="t('web.feedback.enter_your_feedback')"></textarea>
        <div class="mt-2 flex items-center justify-between text-gray-500 dark:text-gray-400">
          <span
            v-if="showCharacterCounter"
            class="text-xs"
            :class="atCharacterLimit
              ? 'text-red-600 dark:text-red-400'
              : 'text-amber-600 dark:text-amber-400'"
            role="status"
            aria-live="polite">
            {{ atCharacterLimit
              ? t('web.feedback.character_limit_reached')
              : t('web.feedback.characters_remaining', { count: charactersRemaining }) }}
          </span>
          <span v-else></span>
          <span v-if="isDesktop">{{ submitWithText }}</span>
        </div>
      </div>

      <!-- Reply-availability notice: heads-up for anonymous submitters -->
      <div
        v-if="!cust?.objid"
        class="rounded-md border border-blue-200 bg-blue-50
          p-3 text-sm text-blue-800
          dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-300"
        role="note">
        {{ t('web.feedback.anonymous_no_reply') }}
      </div>

      <input
        type="hidden"
        name="tz"
        :value="userTimezone" />
      <!-- prettier-ignore-attribute class -->
      <button
        type="submit"
        :disabled="isSubmitting || feedbackMessage == ''"
        class="w-full rounded-md bg-red-600 px-4 py-2 font-medium
          text-white transition-colors hover:bg-red-700
          focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-gray-50
          disabled:cursor-not-allowed disabled:opacity-50 dark:focus:ring-offset-gray-800"
        :aria-label="t('web.feedback.send_feedback')">
        {{ buttonText }}
      </button>
    </form>

    <div class="h-6">
      <div
        v-if="error"
        class="mt-4 text-red-600 dark:text-red-400">
        {{ error }}
      </div>
      <div
        v-if="success"
        class="mt-4 text-green-600 dark:text-green-400">
        {{ success }}
      </div>
    </div>

    <div class="mt-6 text-sm text-gray-500 dark:text-gray-400">
      <h3 class="mb-2 text-lg font-medium text-gray-500 dark:text-gray-400">
        {{ t('web.feedback.when_you_submit_feedback_well_see') }}
      </h3>
      <ul class="space-y-1">
        <!-- Authentication check: cust is null for anonymous users (via AuthenticationSerializer),
             cust.objid confirms a fully hydrated customer object -->
        <li v-if="cust?.objid">
          • {{ t('web.account.customer_id') }}: {{ cust.email }}
        </li>
        <li>
          • {{ t('web.account.timezone') }}: {{ userTimezone }}
        </li>
        <!-- Version is stamped server-side; only listed when this client
             actually knows it (authenticated sessions). -->
        <li v-if="ot_version_long">
          • {{ t('web.site.website_version') }}: {{ ot_version_long }}
        </li>
      </ul>
    </div>
  </div>
</template>
